import type { ComponentProps, PropsWithChildren } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { NetworkStatus, useApolloClient } from '@apollo/client';
import { useGetAdobeManagedAccountDetailsQuery } from '@frameio/graphql-client/graphql/shared/queries/generated/GetAdobeManagedAccountDetails';
import { plural } from '@frameio/i18n';
import { ErrorBoundary, RefreshChip } from '@frameio/shared/components';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { ListLinkWithPrefetch } from '@frameio/shared/routing/components/ListLinkWithPrefetch';
import { useTrack } from '@frameio/shared/segment/hooks';
import { groupByDateFormatted } from '@frameio/shared/utils/groupByDateFormatted';
import { usePerformanceMeasure } from '@frameio/telemetry';
import { Box, List, ListSection, RouterProvider, theme } from '@frameio/vapor';
import { IconArrowClockwise } from '@frameio/vapor-icons';
import { PAGE_SIZE } from '../../constants';
import { useShouldShowTruncatedAccessRequestsList } from '../../contexts/ShouldShowTruncatedAccessRequestsList';
import { GetAllNotificationGroups } from '../../gql';
import {
  useNotificationGroups,
  useReportNotificationGroupsLastOpened,
} from '../../hooks';
import { modifyCachedNotificationGroupsCount } from '../../utils';
import type { GroupedNotificationItems } from '../NotificationCell/types';
import {
  NOTIFICATIONS_LIST_FILTERS,
  type NotificationsListFilters,
} from '../NotificationsListFilter/types';
import {
  EmptyList,
  ErrorFallback,
  LoadingMoreIndicatorError,
} from './components';
import TEXT from './generated/TEXT/NotificationsList';
import { useAccessRequestsGroup } from './hooks/useAccessRequestsGroup';
import { useNotificationListSection } from './hooks/useNotificationListSection';

TEXT.DEFINE({
  /** Text displayed in refresh chip showing new notification count */
  refreshChipCopy: (newCount: number): string => {
    return plural(newCount, 'new notifications', 'new notification');
  },
  /** Accessibility label for refresh chip button */
  refreshChipLabel: (newCount: number): string => {
    return `Refresh to load ${plural(
      newCount,
      'new notifications',
      'new notification'
    )}`;
  },
  /** Accessibility label for the notifications list */
  listAriaLabel: 'Notifications',
  /** Empty state title when filtering by unread */
  unreadEmptyListTitle: 'No unread notifications',
  /** Button to view all notifications from unread empty state */
  unreadEmptyListCta: 'View All',
});

export const NotificationsList = memo(function NotificationsList({
  filter,
  setFilter,
}: {
  filter: NotificationsListFilters;
  setFilter: (filter: NotificationsListFilters) => void;
}) {
  const accountId = useActiveAccountId();
  const client = useApolloClient();

  useGetAdobeManagedAccountDetailsQuery({
    variables: {
      accountId: accountId ?? '',
    },
    skip: !accountId,
  });

  const [forceLoadingIndicator, setForceLoadingIndicator] = useState(false);
  const [fetchNextError, setFetchNextError] = useState(undefined);
  const track = useTrack();
  const renderNotificationListItem = useNotificationListSection({ filter });

  const {
    data,
    error,
    fetchNext,
    getUnopenedCountLoading,
    loading,
    networkStatus,
    newNotificationsCount,
    pageInfo,
  } = useNotificationGroups({ filter });

  const hasNotificationsRef = useRef(false);
  const hasMountedRef = useRef(false);

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
  const isRefetching = networkStatus === NetworkStatus.refetch;

  const listAriaLabel = TEXT.listAriaLabel;
  const unreadEmptyListTitle = TEXT.unreadEmptyListTitle;
  const unreadEmptyListCta = TEXT.unreadEmptyListCta;

  const {
    loading: isReportLastOpenedNotificationLoading,
    reportNotificationGroupsLastOpenedMutation,
  } = useReportNotificationGroupsLastOpened({
    onCompleted: () => setForceLoadingIndicator(false),
  });

  const showLoadingIndicator =
    (loading ||
      forceLoadingIndicator ||
      isReportLastOpenedNotificationLoading ||
      getUnopenedCountLoading ||
      isRefetching) &&
    !isFetchingMore &&
    !error;

  const { endMeasurement: endNotificationListMeasurement } =
    usePerformanceMeasure('notifications_sheet_open', 5000);

  useEffect(() => {
    endNotificationListMeasurement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMore = useCallback(() => {
    if (!showLoadingIndicator && pageInfo?.hasNextPage) {
      fetchNext({
        count: PAGE_SIZE,
        pageInfo,
      })?.catch((err) => {
        setFetchNextError(err);
      });
      track('notifications-fetched-more');
    }
  }, [fetchNext, showLoadingIndicator, pageInfo, track]);

  const reportNotificationGroupsLastOpened = useCallback(async () => {
    /** Optimistically reset the new notifications count so the refresh
     * toast is not shown */
    modifyCachedNotificationGroupsCount({
      accountId,
      client,
      timestamp: null,
      totalCount: 0,
    });

    /** `setForceLoadingIndicator` is set back to `false` in the
     * reportNotificationGroupsLastOpenedMutation's onCompleted
     * callback */
    setForceLoadingIndicator(true);

    const unreadOnly =
      filter === NOTIFICATIONS_LIST_FILTERS.UNREAD ? { unreadOnly: true } : {};

    const refetchData = await client.query({
      query: GetAllNotificationGroups,
      variables: {
        accountId,
        page: {
          first: PAGE_SIZE,
        },
        ...unreadOnly,
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    });
    const lastOpenedNotificationId =
      refetchData?.data?.account?.allNotificationGroups?.mostRecentNotification
        ?.id;

    if (!accountId || !lastOpenedNotificationId) return;

    await reportNotificationGroupsLastOpenedMutation({
      variables: {
        input: {
          accountId,
          lastOpenedNotificationId,
        },
      },
    });
  }, [accountId, client, filter, reportNotificationGroupsLastOpenedMutation]);

  const { groupedAccessRequests, selectedAccessRequestId } =
    useAccessRequestsGroup({ data });

  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (selectedAccessRequestId) {
      setSelected(new Set<string>([selectedAccessRequestId]));
    }
  }, [selectedAccessRequestId]);

  const groupedNotifications = useMemo((): GroupedNotificationItems[] => {
    const nodes = data?.account?.notificationGroups?.nodes ?? [];

    const groups = groupByDateFormatted(nodes, 'mostRecentEventOccurredAt');

    // Convert groups to the expected format
    const groupedItems = Object.entries(groups).map(([key, value]) => {
      return {
        label: key,
        id: key,
        items: value,
      };
    });

    return groupedAccessRequests
      ? [groupedAccessRequests, ...groupedItems]
      : groupedItems;
  }, [data?.account?.notificationGroups?.nodes, groupedAccessRequests]);

  useEffect(() => {
    if (
      !hasNotificationsRef.current &&
      groupedNotifications.length > 0 &&
      filter === NOTIFICATIONS_LIST_FILTERS.ALL
    ) {
      hasNotificationsRef.current = true;
    }
  }, [filter, groupedNotifications]);

  useEffect(() => {
    if (newNotificationsCount > 0 && !hasMountedRef.current) {
      reportNotificationGroupsLastOpened();
    }
    hasMountedRef.current = true;
  }, [newNotificationsCount, reportNotificationGroupsLastOpened]);
  useEffect(() => {
    track('notifications-list-opened', {
      total_count: newNotificationsCount ?? 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Render notification list with loading state, error states,
   * and empty state.
   */

  const notifications = useMemo(() => {
    let loadingState: ComponentProps<typeof List>['loading'];

    if (showLoadingIndicator) {
      loadingState = 'loading';
    } else if (isFetchingMore) {
      loadingState = 'loading-more';
    } else if (fetchNextError) {
      loadingState = 'loading-more-error';
    } else if (error) {
      loadingState = 'loading-error';
    }
    return (
      <List
        animate={true}
        aria-label={listAriaLabel}
        estimatedItemHeight={64}
        itemGap={8}
        items={groupedNotifications}
        loading={loadingState}
        onBoundary={(boundary) => {
          if (boundary === 'bottom' && groupedNotifications.length > 0) {
            fetchMore();
          }
        }}
        renderLoading={(state) => {
          if (state === 'loading-error') {
            return <ErrorFallback />;
          }

          if (state === 'loading-more-error') {
            return <LoadingMoreIndicatorError />;
          }

          // return null to delegate List loading state to the List component
          return null;
        }}
        renderPlaceholder={() => {
          const text =
            filter === NOTIFICATIONS_LIST_FILTERS.UNREAD
              ? {
                  emptyListTitle: unreadEmptyListTitle,
                  emptyListCta: unreadEmptyListCta,
                }
              : undefined;

          const onPress =
            filter === NOTIFICATIONS_LIST_FILTERS.UNREAD
              ? () => setFilter(NOTIFICATIONS_LIST_FILTERS.ALL)
              : undefined;

          return (
            <EmptyList
              onPress={onPress}
              shouldRender={
                filter === NOTIFICATIONS_LIST_FILTERS.UNREAD ||
                !hasNotificationsRef.current
              }
              text={text}
            />
          );
        }}
        selectedKeys={selected}
        selectionMode="single"
        variant="list"
      >
        {(section) => {
          return (
            <ListSection
              headerBackground={theme.colors.components.sheet.background}
              isHeaderSticky={true}
              isHeaderVisible={true}
              items={section.items}
              title={section.label}
            >
              {renderNotificationListItem}
            </ListSection>
          );
        }}
      </List>
    );
  }, [
    error,
    fetchMore,
    fetchNextError,
    filter,
    hasNotificationsRef,
    setFilter,
    groupedNotifications,
    isFetchingMore,
    showLoadingIndicator,
    renderNotificationListItem,
    selected,
    listAriaLabel,
    unreadEmptyListTitle,
    unreadEmptyListCta,
  ]);
  const { setShouldShowTruncatedAccessRequestsList } =
    useShouldShowTruncatedAccessRequestsList();
  return (
    <ErrorBoundary
      FallbackComponent={(props) => (
        <ErrorFallback key="notifications-list-error-fallback" {...props} />
      )}
    >
      <Box
        data-testid="notifications-list"
        display="flex"
        flexDirection="column"
        height="100%"
        overflow="hidden"
      >
        <RefreshChip
          copy={TEXT.refreshChipCopy(newNotificationsCount)}
          count={newNotificationsCount}
          dataTestId="notifications-refresh-chip"
          iconBefore={<IconArrowClockwise />}
          label={TEXT.refreshChipLabel(newNotificationsCount)}
          motionKey="notifications-refresh-chip"
          onClick={async () => {
            await reportNotificationGroupsLastOpened();
            await track('notifications-refresh-chip-actioned', {
              total_count: newNotificationsCount ?? 0,
            });
            setShouldShowTruncatedAccessRequestsList(
              hasNotificationsRef.current
            );
          }}
          visible={!showLoadingIndicator && newNotificationsCount > 0}
        />
        <ListLinkProvider>{notifications}</ListLinkProvider>
      </Box>
    </ErrorBoundary>
  );
});

function ListLinkProvider(props: PropsWithChildren) {
  const router = useRouter();
  return (
    <RouterProvider
      LinkComponent={ListLinkWithPrefetch as unknown as React.ComponentType}
      navigate={router.push}
      {...props}
    />
  );
}
